import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { UploadCloud, Loader2, FileText, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";

/**
 * AdminCsvImportCard
 * Admin-only card that lets the operator drop a CSV of businesses in and
 * POSTs to `/admin/businesses/bulk-import`. Renders a compact result panel
 * with counts + a preview of the first 25 inserted rows.
 *
 * Also exposes a "Backfill descriptions" button which scrapes every
 * placeholder-description business's website for its OpenGraph/meta
 * description — no external services, just each site's own HTML.
 *
 * Accepts common column names (business_name/name/company, category, address,
 * city, state, phone, website, email, description) — headers are matched
 * case-insensitively so the same CSV format used by Google-Places exports
 * works out of the box.
 */
export default function AdminCsvImportCard() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      toast.error("Please pick a .csv file");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const runImport = async () => {
    if (!file) {
      toast.error("Choose a CSV first");
      return;
    }
    if (!window.confirm(
      `Import "${file.name}"? Every valid row will be added to your directory as an unverified listing. Duplicates by name+state are skipped automatically.`
    )) return;

    setRunning(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await axios.post(
        `${API}/admin/businesses/bulk-import`,
        form,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      setResult(r.data);
      toast.success(
        `Imported ${r.data.inserted_count} · Skipped ${r.data.skipped_duplicates} duplicates`
      );
      // Clear file so a fresh pick is required to re-run
      setFile(null);
      if (inputRef.current) inputRef.current.value = "";
    } catch (e) {
      const detail = e.response?.data?.detail || "CSV import failed";
      toast.error(detail);
    } finally {
      setRunning(false);
    }
  };

  const runBackfill = async () => {
    if (!window.confirm(
      "Scrape every business's website for its meta / OpenGraph description?\n\nOnly rows whose current description is empty or the auto-placeholder (\"Black-owned … business.\") are touched. This can take up to a minute for large imports."
    )) return;

    setBackfilling(true);
    setBackfillResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/businesses/backfill-descriptions`,
        {},
        { withCredentials: true }
      );
      setBackfillResult(r.data);
      toast.success(
        `Backfilled ${r.data.updated_count} description${r.data.updated_count === 1 ? "" : "s"} (${r.data.failed_count || 0} skipped)`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill failed");
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <div
      className="bg-white rounded-xl shadow-sm p-5 border border-gray-100"
      data-testid="admin-csv-import-card"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-lg bg-tbc-gold/15 flex items-center justify-center flex-shrink-0">
          <UploadCloud className="w-4 h-4 text-tbc-gold" />
        </div>
        <div>
          <h3 className="font-heading text-tbc-navy">Bulk CSV Import</h3>
          <p className="text-xs text-gray-500 font-body mt-0.5">
            Drop a spreadsheet of Black-owned businesses. We map free-form
            categories to TBC categories, de-dupe by name + state, and land
            everything as unverified listings so you can review before
            publishing.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <label
          className="flex items-center gap-3 border-2 border-dashed border-gray-200 rounded-lg p-3 cursor-pointer hover:border-tbc-gold hover:bg-tbc-cream/30 transition-colors"
          data-testid="csv-file-drop"
        >
          <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-600 truncate flex-1">
            {file ? file.name : "Choose a .csv file…"}
          </span>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={onPick}
            data-testid="csv-file-input"
          />
        </label>

        <Button
          onClick={runImport}
          disabled={running || !file}
          className="bg-tbc-navy hover:bg-tbc-navy/90 text-white w-full"
          data-testid="csv-import-run"
        >
          {running ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing…
            </>
          ) : (
            "Import businesses"
          )}
        </Button>

        <p className="text-[11px] text-gray-400 font-body leading-relaxed">
          Recognised columns (case-insensitive): <em>business_name / name</em>,{" "}
          <em>category</em>, <em>address</em>, <em>city</em>, <em>state</em>,{" "}
          <em>phone</em>, <em>website</em>, <em>email</em>,{" "}
          <em>description</em>.
        </p>

        {result && (
          <div
            className="mt-2 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800 font-body"
            data-testid="csv-import-result"
          >
            <div className="flex items-center gap-2 font-semibold">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {result.inserted_count} imported ·{" "}
              {result.skipped_duplicates} duplicates skipped
              {result.invalid_rows?.length
                ? ` · ${result.invalid_rows.length} invalid`
                : ""}
            </div>

            {result.inserted?.length > 0 && (
              <ul className="mt-2 max-h-40 overflow-y-auto space-y-0.5">
                {result.inserted.map((b) => (
                  <li key={b.business_id || b.name} className="truncate">
                    &bull; {b.name} <span className="text-emerald-600">— {b.category}</span>
                    {b.city ? ` · ${b.city}, ${b.state}` : ""}
                  </li>
                ))}
                {result.inserted_count > result.inserted.length && (
                  <li className="text-emerald-700">
                    …and {result.inserted_count - result.inserted.length} more
                  </li>
                )}
              </ul>
            )}

            {result.invalid_rows?.length > 0 && (
              <div className="mt-3 flex items-start gap-2 text-amber-700">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <div className="text-[11px]">
                  Skipped rows:{" "}
                  {result.invalid_rows
                    .slice(0, 5)
                    .map((r) => `row ${r.row}: ${r.reason}`)
                    .join("; ")}
                  {result.invalid_rows.length > 5
                    ? ` …+${result.invalid_rows.length - 5} more`
                    : ""}
                </div>
              </div>
            )}

            <p className="mt-3 text-[11px] text-emerald-700">
              Imported rows land as <strong>unverified</strong>. Head to the
              list below (or the Verifications tab) to review and publish.
            </p>
          </div>
        )}

        {/* Backfill missing descriptions — scrapes each site's meta tag. Safe
            to re-run: only touches rows whose description is empty or the
            auto-placeholder from the CSV importer. */}
        <div className="pt-3 mt-3 border-t border-gray-100">
          <Button
            onClick={runBackfill}
            disabled={backfilling}
            variant="outline"
            className="w-full border-tbc-gold text-tbc-navy hover:bg-tbc-gold/10"
            data-testid="csv-backfill-descriptions"
          >
            {backfilling ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scraping websites…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" /> Backfill missing descriptions
              </>
            )}
          </Button>
          <p className="text-[11px] text-gray-400 font-body mt-2 leading-relaxed">
            Pulls each business&rsquo;s official OpenGraph / meta description
            from their own website. Skips rows that already have a real
            description. Safe to re-run anytime.
          </p>

          {backfillResult && (
            <div
              className="mt-3 rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 font-body"
              data-testid="csv-backfill-result"
            >
              <div className="flex items-center gap-2 font-semibold">
                <CheckCircle2 className="w-3.5 h-3.5" />
                {backfillResult.updated_count} updated ·{" "}
                {backfillResult.failed_count || 0} skipped
                {backfillResult.candidates
                  ? ` (of ${backfillResult.candidates} candidates)`
                  : ""}
              </div>
              {backfillResult.message && (
                <p className="mt-1 text-blue-800">{backfillResult.message}</p>
              )}
              {backfillResult.failed?.length > 0 && (
                <ul className="mt-2 max-h-32 overflow-y-auto space-y-0.5 text-[11px] text-blue-700">
                  {backfillResult.failed.slice(0, 8).map((f) => (
                    <li key={f.business_id || `${f.name}-${f.reason}`} className="truncate">
                      &bull; {f.name}: {f.reason}
                    </li>
                  ))}
                  {backfillResult.failed.length > 8 && (
                    <li>…and {backfillResult.failed.length - 8} more</li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
